import React, { useCallback, useEffect, useMemo } from 'react'
import { useField, useFormikContext } from 'formik'
import CodeMirror from '@uiw/react-codemirror'
import { json } from '@codemirror/lang-json'
import { javascript } from '@codemirror/lang-javascript'
import { html } from '@codemirror/lang-html'
import { Decoration, DecorationSet, EditorView } from '@codemirror/view'
import { Extension, StateField } from '@codemirror/state'
import { debounce } from 'lodash'
import { TComponentCodeEditor } from './_types'
import { useThemeMode } from '../../../../_metronic/partials'

export const ComponentCodeEditor: React.FC<TComponentCodeEditor> = ({
    article,
    is_disabled,
    customHandler,
    language = 'json',
    debounceTime = 300,
}) => {
    const [field] = useField(article)
    const { setFieldValue } = useFormikContext()
    const { value } = field
    const theme = useThemeMode()

    // Автоматическая подстановка шаблона для JSON
    useEffect(() => {
        if (language === 'json' && (!value || value.trim() === '')) {
            const template = `{
    "object":"",
    "command":"",
    "data": {
        
    }
}`
            if (customHandler) {
                customHandler(template)
            } else {
                setFieldValue(article, template)
            }
        }
    }, [language, value, customHandler, setFieldValue, article])

    const debouncedChange = useMemo(
        () =>
            debounce((val: string) => {
                if (customHandler) {
                    customHandler(val)
                } else {
                    setFieldValue(article, val)
                }
            }, debounceTime),
        [customHandler, setFieldValue, article, debounceTime]
    )

    const handleChange = useCallback(
        (val: string) => {
            debouncedChange(val)
        },
        [debouncedChange]
    )

    const errorDecoration = useMemo(() => {
        return StateField.define<DecorationSet>({
            create() {
                return Decoration.none
            },
            update(_, transaction) {
                const text = transaction.state.doc.toString()
                if (!text) return Decoration.none

                if (language === 'json') {
                    try {
                        JSON.parse(text)
                        return Decoration.none
                    } catch (err: unknown) {
                        let pos = transaction.state.doc.length

                        if (err instanceof SyntaxError && err.message) {
                            const match = /position (\d+)/.exec(err.message)
                            if (match && match[1]) {
                                pos = Math.min(parseInt(match[1], 10), transaction.state.doc.length)
                            }
                        }

                        const line = transaction.state.doc.lineAt(pos)
                        return Decoration.set([
                            Decoration.mark({
                                class: 'cm-error-underline',
                                attributes: {
                                    title:
                                        err instanceof SyntaxError ? err.message : 'Syntax error',
                                },
                            }).range(line.from, line.to),
                        ])
                    }
                }
                return Decoration.none
            },
            provide: (f) => EditorView.decorations.from(f),
        })
    }, [language])

    const getExtension = useCallback((): Extension[] => {
        switch (language) {
            case 'html':
                return [html()]
            case 'javascript':
                return [javascript({ jsx: true })]
            default:
                return [json()]
        }
    }, [language])

    return (
        <div className='componentCodeEditor'>
            <CodeMirror
                value={value ?? ''}
                extensions={[...getExtension(), errorDecoration]}
                editable={!is_disabled}
                height='auto'
                theme={theme.mode === 'light' ? 'light' : 'dark'}
                basicSetup={{
                    lineNumbers: true,
                    highlightActiveLine: true,
                    highlightActiveLineGutter: true,
                    bracketMatching: true,
                    autocompletion: true,
                    foldGutter: true,
                }}
                onChange={handleChange}
            />
        </div>
    )
}

export default ComponentCodeEditor
